// src/services/sessionCleanupHelpers.js - Pure utility functions for session cleanup
// Extracted from PlatformSelector.js and Dashboard.js

import logger from '../services/logger';
import { API_BASE_URL } from '../config';

/**
 * Stop Spotify Web Player with comprehensive cleanup
 */
export const stopSpotifyWebPlayer = async () => {
  if (window.Spotify && window.spotifyPlayer) {
    try {
      await window.spotifyPlayer.pause();
      await window.spotifyPlayer.seek(0); // Reset position
      logger.info('⏸️ Spotify paused via existing SDK controls');
    } catch (err) {
      logger.warn('Could not pause Spotify player:', err);
    }
  }
};

/**
 * Stop all HTML audio and video elements
 */
export const stopHtmlAudioElements = () => {
  const audioElements = document.querySelectorAll('audio, video');
  audioElements.forEach(element => {
    element.pause();
    element.currentTime = 0;
    element.src = '';
    element.load(); // Force reload to clear cached data
  });
  logger.debug('HTML audio elements stopped');
};

/**
 * Stop YouTube playback with enhanced cleanup
 */
export const stopYouTubePlayback = () => {
  // Stop embedded YouTube players
  if (window.YT && window.YT.PlayerState) {
    const players = document.querySelectorAll('iframe[src*="youtube.com"]');
    players.forEach(player => {
      try {
        if (player.contentWindow) {
          player.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          player.contentWindow.postMessage('{"event":"command","func":"stopVideo","args":""}', '*');
          player.contentWindow.postMessage('{"event":"command","func":"seekTo","args":[0, true]}', '*');
        }
      } catch (err) {
        logger.warn('Could not pause YouTube player:', err);
      }
    });
  }

  // Stop YouTube iframes by clearing src
  document.querySelectorAll('iframe[src*="youtube"]').forEach(iframe => {
    iframe.src = 'about:blank';
  });

  logger.debug('YouTube playback stopped');
};

/**
 * Clear all music-related timers and intervals
 */
export const clearMusicTimers = () => {
  // Clear Spotify-specific intervals
  if (window.spotifyProgressInterval) {
    clearInterval(window.spotifyProgressInterval);
    window.spotifyProgressInterval = null;
  }
  
  // Clear track end timeouts
  if (window.trackEndTimeout) {
    clearTimeout(window.trackEndTimeout);
    window.trackEndTimeout = null;
  }
  
  // Clear YouTube-specific timers
  if (window.youtubeProgressInterval) {
    clearInterval(window.youtubeProgressInterval);
    window.youtubeProgressInterval = null;
  }
  
  // Clear any custom music timers
  if (window.musicTimers) {
    window.musicTimers.forEach(timer => clearTimeout(timer));
    window.musicTimers = [];
  }
  
  // Clear animation frames related to audio visualization
  if (window.audioAnimationFrame) {
    cancelAnimationFrame(window.audioAnimationFrame);
    window.audioAnimationFrame = null;
  }

  logger.debug('All music timers cleared');
};

/**
 * Clear all platform sessions for security
 */
export const clearPlatformSessions = async (supabaseToken = null) => {
  const headers = supabaseToken
    ? { 'X-Supabase-Token': supabaseToken }
    : {};

  await Promise.allSettled([
    fetch(`${API_BASE_URL}/logout?platform=spotify`, {
      method: 'GET',
      credentials: 'include',
      headers
    }),
    fetch(`${API_BASE_URL}/logout?platform=youtube`, {
      method: 'GET',
      credentials: 'include',
      headers
    }),
    fetch(`${API_BASE_URL}/logout`, {
      method: 'GET',
      credentials: 'include',
      headers
    })
  ]);
  logger.debug('Platform sessions + Supabase tokens cleared');
};

/**
 * Clear all local storage related to music and sessions
 */
export const clearAllStorage = () => {
  // Clear session and auth tokens
  localStorage.removeItem('guestSession');
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  
  // Clear Spotify SDK specific storage
  if (localStorage.getItem('spotify-sdk-token')) {
    localStorage.removeItem('spotify-sdk-token');
  }
  
  // Clear any music-related storage
  const musicKeys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      key.includes('spotify') || 
      key.includes('youtube') || 
      key.includes('player') ||
      key.includes('music')
    )) {
      musicKeys.push(key);
    }
  }
  musicKeys.forEach(key => localStorage.removeItem(key));
  
  logger.debug('Local storage cleared');
};

/**
 * Clean up Web Audio API contexts
 */
export const cleanupWebAudioContexts = async () => {
  try {
    // Close any active audio contexts
    if (window.audioContext && window.audioContext.state !== 'closed') {
      await window.audioContext.close();
      logger.debug('Audio context closed');
    }
    
    // Clean up any analyzer nodes
    if (window.audioAnalyzer) {
      window.audioAnalyzer.disconnect();
      window.audioAnalyzer = null;
    }
    
    // Clean up gain nodes
    if (window.audioGainNode) {
      window.audioGainNode.disconnect();
      window.audioGainNode = null;
    }

    // Close any Web Audio API contexts array
    if (window.AudioContext || window.webkitAudioContext) {
      if (window.audioContexts) {
        const closePromises = window.audioContexts
          .filter(ctx => ctx.state !== 'closed')
          .map(ctx => ctx.close());
        await Promise.all(closePromises);
      }
    }
    
  } catch (audioErr) {
    logger.warn('Web Audio cleanup warning:', audioErr);
  }
};

/**
 * Reset UI elements and visual states
 */
export const resetUIElements = () => {
  // Reset any progress bars
  const progressBars = document.querySelectorAll('.progress-bar, .seek-bar');
  progressBars.forEach(bar => {
    bar.style.width = '0%';
    bar.setAttribute('aria-valuenow', '0');
  });
  
  // Reset play buttons to play state
  const playButtons = document.querySelectorAll('.play-button, .pause-button');
  playButtons.forEach(button => {
    button.classList.remove('playing', 'paused');
    button.classList.add('play');
  });
  
  // Reset volume sliders to default
  const volumeSliders = document.querySelectorAll('.volume-slider');
  volumeSliders.forEach(slider => {
    slider.value = slider.defaultValue || 50;
  });

  logger.debug('UI states reset');
};

/**
 * Clean up music-related event listeners
 */
export const cleanupMusicEventListeners = () => {
  try {
    // Remove Spotify player event listeners
    if (window.spotifyPlayer) {
      window.spotifyPlayer.removeListener('ready');
      window.spotifyPlayer.removeListener('not_ready');
      window.spotifyPlayer.removeListener('player_state_changed');
    }
    
    // Remove custom music event listeners
    document.removeEventListener('music-player-stop', window.musicPlayerStopHandler);
    document.removeEventListener('track-ended', window.trackEndedHandler);
    
    logger.debug('Event listeners cleaned up');
  } catch (listenerErr) {
    logger.warn('Event listener cleanup warning:', listenerErr);
  }
};

/**
 * Emergency cleanup - force stop everything
 */
export const emergencyCleanup = () => {
  logger.warn('🚨 Performing emergency cleanup...');
  
  try {
    // Emergency stop all media
    document.querySelectorAll('audio, video, iframe').forEach(element => {
      try {
        if (element.tagName === 'IFRAME') {
          element.src = 'about:blank';
        } else {
          element.pause();
          element.src = '';
          element.load();
        }
      } catch (emergencyErr) {
        // Ignore errors in emergency cleanup
      }
    });
    
    // Emergency clear timers
    const highestTimeoutId = setTimeout(() => {}, 0);
    for (let i = 0; i < highestTimeoutId; i++) {
      clearTimeout(i);
      clearInterval(i);
    }
    
    // Clear all storage as emergency measure
    localStorage.clear();
    sessionStorage.clear();
    
    logger.success('Emergency cleanup completed');
  } catch (emergencyCleanupErr) {
    logger.warn('Emergency cleanup error:', emergencyCleanupErr);
  }
};

/**
 * Comprehensive cleanup combining all cleanup functions
 */
export const performComprehensiveCleanup = async () => {
  logger.debug('🎵 Starting comprehensive cleanup...');
  
  try {
    // Stop all music playback
    await stopSpotifyWebPlayer();
    stopHtmlAudioElements();
    stopYouTubePlayback();
    
    // Clear timers and intervals
    clearMusicTimers();
    
    // Clean up Web Audio API
    await cleanupWebAudioContexts();
    
    // Reset UI elements
    resetUIElements();
    
    // Clean up event listeners
    cleanupMusicEventListeners();
    
    // Wait for all operations to complete
    await new Promise(resolve => setTimeout(resolve, 200));
    
    logger.success('Spotify Web Player cleanup completed');
    
  } catch (error) {
    logger.error('Error during comprehensive cleanup:', error);
    
    // Fall back to emergency cleanup
    emergencyCleanup();
  }
};